import Typography from '@mui/material/Typography';
import { useBlockingSubscription } from 'features/user';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { DeleteAccountDialog } from 'shared/dialogs/delete-account';

import * as Styled from './settings-form.style';
import { UserName } from './user-name';

export type AccountSettingsProps = {
  name: string;
  onChangeName: (name: string) => void;
  email: string;
  isSignedInWithProvider?: boolean;
  onChangePassword: () => void;
};

export const AccountSettings: React.FC<AccountSettingsProps> = ({
  name,
  onChangeName,
  email,
  isSignedInWithProvider,
  onChangePassword,
}) => {
  const { t } = useTranslation();
  const blockingSubscription = useBlockingSubscription();
  const [isDeleteAccountOpen, setDeleteAccountOpen] = useState(false);

  const onOpenDeleteAccount = () => setDeleteAccountOpen(true);
  const onCloseDeleteAccount = () => setDeleteAccountOpen(false);

  return (
    <>
      <Styled.Section>
        <Typography variant="h6" component="label" htmlFor="name">
          {t('forms.user-settings.sections.name.label')}
        </Typography>

        <UserName id="name" value={name} onChange={onChangeName} />
      </Styled.Section>

      <Styled.Section>
        <Typography variant="h6" component="span">
          {t('forms.user-settings.sections.security.label')}
        </Typography>

        {isSignedInWithProvider ? (
          <Styled.SubSection>
            <Typography variant="body2" component="p">
              {t('forms.user-settings.sections.security.siwa.title')}
            </Typography>
            <Styled.SecondaryTextTypography variant="overline">
              {email}
            </Styled.SecondaryTextTypography>
          </Styled.SubSection>
        ) : (
          <>
            <Styled.SubSection>
              <Typography variant="body2" component="p">
                {t('forms.user-settings.sections.security.email.title')}
              </Typography>
              <Styled.SecondaryTextTypography variant="overline" $canCopy>
                {email}
              </Styled.SecondaryTextTypography>
            </Styled.SubSection>
            <Styled.SplitSectionRow>
              <Typography variant="body2" component="span">
                {t('forms.user-settings.sections.security.password.label')}
              </Typography>
              <Button
                onClick={onChangePassword}
                size={Sizes.Small}
                variant={Variants.Outlined}
                color={Colors.Inherit}
              >
                {t('forms.user-settings.sections.security.password.button')}
              </Button>
            </Styled.SplitSectionRow>
          </>
        )}
      </Styled.Section>

      <Styled.Section>
        <Typography variant="h6" component="label">
          {t('forms.user-settings.sections.delete-account.label')}
        </Typography>
        <Styled.SecondaryTextTypography variant="overline">
          {t('forms.user-settings.sections.delete-account.description')}
        </Styled.SecondaryTextTypography>
        <Styled.SplitSectionRow>
          <Button
            size={Sizes.Small}
            variant={Variants.Outlined}
            color={Colors.Error}
            onClick={onOpenDeleteAccount}
          >
            {t('forms.user-settings.sections.delete-account.button.label')}
          </Button>
        </Styled.SplitSectionRow>
      </Styled.Section>

      <DeleteAccountDialog
        open={isDeleteAccountOpen}
        blockingSubscription={blockingSubscription}
        onClose={onCloseDeleteAccount}
      />
    </>
  );
};
